import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface LeagueAvailabilityZone {
  id: string;
  league_id: string;
  name: string;
  court_ids: string[];
  day_of_week: number[];
  start_time: string;
  end_time: string;
  valid_from: string | null;
  valid_to: string | null;
  created_at: string;
}

export interface ZoneSlot {
  time: string;
  zoneName: string;
  courts: { id: string; name: string }[];
}

export function useLeagueZones(leagueId: string | undefined) {
  const queryClient = useQueryClient();

  const zonesQuery = useQuery({
    queryKey: ["league-availability-zones", leagueId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("league_availability_zones")
        .select("*")
        .eq("league_id", leagueId!)
        .order("name");
      if (error) throw error;
      return (data ?? []) as LeagueAvailabilityZone[];
    },
    enabled: !!leagueId,
  });

  const createZone = useMutation({
    mutationFn: async (zone: Omit<LeagueAvailabilityZone, "id" | "created_at">) => {
      const { error } = await (supabase as any)
        .from("league_availability_zones")
        .insert(zone);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["league-availability-zones", leagueId] });
    },
  });

  const updateZone = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<LeagueAvailabilityZone> & { id: string }) => {
      const { error } = await (supabase as any)
        .from("league_availability_zones")
        .update(updates)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["league-availability-zones", leagueId] });
    },
  });

  const deleteZone = useMutation({
    mutationFn: async (zoneId: string) => {
      const { error } = await (supabase as any)
        .from("league_availability_zones")
        .delete()
        .eq("id", zoneId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["league-availability-zones", leagueId] });
    },
  });

  return {
    zones: zonesQuery.data ?? [],
    isLoading: zonesQuery.isLoading,
    createZone,
    updateZone,
    deleteZone,
  };
}

/**
 * Generate available time slots from zones for a given date.
 * Checks real court availability against existing bookings and skips past times.
 * Only returns slots where the full duration fits within the zone window.
 */
export function getAvailableZoneSlots(
  zones: LeagueAvailabilityZone[],
  date: Date,
  durationMinutes: number,
  courts: { id: string; name: string }[],
  existingBookings: { court_id: string; start_time: string; end_time: string }[]
): ZoneSlot[] {
  const now = new Date();
  // JS getDay: 0=Sun, 1=Mon ... 6=Sat → convert to ISO: 1=Mon ... 7=Sun
  const jsDay = date.getDay();
  const isoDay = jsDay === 0 ? 7 : jsDay;
  const dateStr = date.toISOString().slice(0, 10);

  const slots: ZoneSlot[] = [];

  for (const zone of zones) {
    // Check day_of_week match (stored as ISO weekday: 1=Mon...7=Sun)
    if (!zone.day_of_week.includes(isoDay)) continue;

    // Check validity date range
    if (zone.valid_from && dateStr < zone.valid_from) continue;
    if (zone.valid_to && dateStr > zone.valid_to) continue;

    // Filter courts that belong to this zone and exist in the provided courts list
    const zoneCourts = courts.filter((c) => zone.court_ids.includes(c.id));
    if (zoneCourts.length === 0) continue;

    // Parse zone time window
    const [startH, startM] = zone.start_time.split(":").map(Number);
    const [endH, endM] = zone.end_time.split(":").map(Number);
    const zoneStartMin = startH * 60 + startM;
    const zoneEndMin = endH * 60 + endM;

    // Generate 30-minute interval slots within the zone window
    for (let min = zoneStartMin; min + durationMinutes <= zoneEndMin; min += 30) {
      const slotStart = new Date(date);
      slotStart.setHours(Math.floor(min / 60), min % 60, 0, 0);
      const slotEnd = new Date(slotStart.getTime() + durationMinutes * 60 * 1000);

      // Skip past times
      if (slotStart < now) continue;

      // Check which courts are free
      const freeCourts = zoneCourts.filter((court) => {
        return !existingBookings.some(
          (b) =>
            b.court_id === court.id &&
            new Date(b.start_time) < slotEnd &&
            new Date(b.end_time) > slotStart
        );
      });

      if (freeCourts.length > 0) {
        const timeStr = `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
        slots.push({ time: timeStr, zoneName: zone.name, courts: freeCourts });
      }
    }
  }

  return slots;
}
